/**
 * 画像ポップアップ v2 (vanilla, 外部ライブラリ非依存)
 *
 * 機能:
 *   - .image-popup / .img-popup / .gallery-item img をクリックでオーバーレイ表示
 *   - 同じ gallery 内であれば prev/next、左右キー、横スワイプ/ドラッグで連続閲覧
 *   - ESC / 画像以外をクリック・タップ / × ボタンで閉じる
 *   - figure > figcaption > data-caption > alt の優先順位でキャプション表示
 *   - prefers-reduced-motion でアニメ無効
 *   - gp-lazy-img の lazy load (IntersectionObserver, rootMargin 1500px) + 保険スイープ
 *
 * 設計の核 (大手 carousel 同等の安定性を目指す):
 *   - **正規化**: 対象画像はすべて <a class="image-popup img-popup"> の直下に正規化される
 *     既存 <a class="image-popup"> も、裸の <img class="image-popup"> も、同じ DOM 形に揃える
 *   - **遅延参照**: gallery items はアンカーの DOM 参照のみを持ち、表示時に毎回 a.href を解決
 *     → lazy upgrade で href が後から更新されても自動的に追随する
 *   - **isAnimating ロック**: スワイプ/click/key 由来の navigate はアニメ中は無視
 *   - **フリック判定**: pointerup 時に distance または velocity でフリック判定
 *   - **端での抵抗感**: 先頭/末尾でさらに引っ張ると追従量を 1/3 に減衰
 *   - **プリロード**: navigate 時に裏で new Image() を発火、スライド演出は即時
 *   - **3-frame バッファ + スピナー**: prev/current/next を <img> 使い回しで GC 負荷を抑える
 *
 * 公開 API:
 *   - MokoImagePopup.scan(scope)         動的 DOM 用
 *   - MokoImagePopup.refresh()           スキャン + lazy refresh
 *   - MokoImagePopup.lazyRefresh(scope)  display:none → block 切替時に呼ぶ
 *   - MokoImagePopup.open(galleryId, i)  外部から開く
 *   - MokoImagePopup.close()
 */
(function () {
  'use strict';

  // ---------------------------- 定数 ----------------------------
  // selector はユーザ指示に基づき以下 3 条件で拾う:
  //   (a) <a class="image-popup"> / <a class="img-popup"> がある (= リンクとして明示)
  //   (b) <img class="image-popup"> / <img class="img-popup"> がある (= 画像として明示)
  //   (c) WordPress 標準の <a href="原寸"><img src="サムネ" ...></a> パターン
  //       href と img の src が「同一画像のサイズ違い」(originalSrc 正規化後一致) のとき、
  //       クラス指定がなくてもポップアップ対象として扱う。
  //       (= 一致しないリンクは normalizeTarget() の (a) パスで弾くので、 別ページへの
  //       リンク画像が誤って拾われることはない)。
  // WP デフォルトの `.wp-image-*` や `.gallery-item img` を「暗黙に拾う」既存挙動は
  // 廃止する (中央寄せ等の記事内ブロック CSS と競合し、 意図せぬ画像まで拡大対象になるため)。
  // (c) 検出のため selector には `a:has(img)` を追加 (子孫 img 全般。 直接子に限定すると
  // <a><span><img></span></a> のような WP エディタ由来のネスト構造を取り逃がす)。
  // :has 非対応環境向けには scan() 側で a[href] を別途走査するフォールバックがある。
  var CONFIG = {
    selector: 'a.image-popup, a.img-popup, img.image-popup, img.img-popup, a:has(img)',
    excludeSelector: '.exclusion-popup, .no-popup',
    lazySelector: 'img.gp-lazy-img',
    rootMargin: '1500px 0px',
    swipeDistanceThreshold: 50,     // px - これ以上ドラッグで遷移
    swipeVelocityThreshold: 0.5,    // px/ms - これ以上の速度ならフリック判定で遷移
    swipeAxisLock: 6,               // px - 縦横確定までの遊び
    resistanceAtEdge: 0.3,          // 端でさらに引っ張った時の追従減衰
    initedAttr: 'data-moko-popup-inited',
    galleryAttr: 'data-moko-popup-gallery',
    indexAttr:   'data-moko-popup-index'
  };
  var ROOT_CLASS = 'moko-image-popup';
  var DEFAULT_GALLERY_ID = 'default';

  // ---------------------------- util ----------------------------
  // WordPress が生成する画像 URL の派生バリエーションを正規化して
  // 「同一画像」を判定可能にする。 想定する派生:
  //   1. size suffix: foo-480x270.jpg ↔ foo.jpg
  //   2. -scaled suffix: foo-scaled.jpg ↔ foo.jpg (big_image_size_threshold 由来)
  //   3. CDN resize クエリ: foo.png?resize=480,270 / ?w=480&h=270 等
  //   4. HTML エンティティ: post_content 中の &amp; が URL に残るケース
  //   5. Jetpack Photon の CDN シャード: i0.wp.com / i1.wp.com / i2.wp.com を等価視
  //   6. プロトコル相対 // → そのまま (origin の違いは別軸)
  function originalSrc(url) {
    if (!url) return url;
    return url
      .replace(/&amp;/g, '&')                                 // HTML エンティティを decode (post_content 経由で残る場合がある)
      .replace(/-\d+x\d+(\.[a-zA-Z0-9]+)(\?|$)/, '$1$2')      // size suffix (-480x270)
      .replace(/-scaled(\.[a-zA-Z0-9]+)(\?|$)/, '$1$2')       // WP big_image -scaled suffix
      .replace(/([?&])(resize|w|h|width|height|ssl|quality|strip|fit|crop)=[^&]*/g, '$1') // CDN リサイズ系クエリ
      .replace(/^(https?:\/\/)i\d+\.wp\.com\//, '$1i.wp.com/') // Photon CDN シャード (i0/i1/i2.wp.com) を i.wp.com に統一
      .replace(/[?&]+$/, '')
      .replace(/\?&+/g, '?')
      .replace(/&&+/g, '&');
  }

  function isExcluded(el) {
    if (!el) return true;
    if (el.matches && el.matches(CONFIG.excludeSelector)) return true;
    if (el.closest && el.closest(CONFIG.excludeSelector)) return true;
    return false;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clampIndex(index, length) {
    var parsed = Number(index);
    if (!Number.isFinite(parsed) || length <= 0) return 0;
    return Math.max(0, Math.min(length - 1, Math.floor(parsed)));
  }

  // figure > figcaption > data-caption > alt の優先順位でキャプションを取る。
  // どれも空・無しなら空文字を返す (呼び出し側でキャプション領域を display:none する)
  function resolveCaption(anchor) {
    if (!anchor) return '';
    var fig = anchor.closest && anchor.closest('figure');
    if (fig) {
      var fc = fig.querySelector('figcaption');
      if (fc) {
        var fcText = fc.textContent.trim();
        if (fcText !== '') return fcText;
      }
    }
    var dc = anchor.getAttribute && anchor.getAttribute('data-caption');
    if (dc && dc.trim() !== '') return dc.trim();
    var img = anchor.querySelector && anchor.querySelector('img');
    if (img) {
      var imgDc = img.getAttribute('data-caption');
      if (imgDc && imgDc.trim() !== '') return imgDc.trim();
      var alt = img.getAttribute('alt');
      if (alt && alt.trim() !== '') return alt.trim();
    }
    return '';
  }

  // anchor から「現時点で最新の」画像 URL を解決する。
  // lazy upgrade 後は a.href 自体が更新されているのでそれを使い、
  // 古い data:image placeholder を踏まないようにする。
  function resolveSrcFromAnchor(anchor) {
    if (!anchor) return null;
    if (anchor.tagName === 'A' && anchor.getAttribute('href')) {
      return anchor.getAttribute('href');
    }
    var img = anchor.querySelector && anchor.querySelector('img');
    if (img) {
      var orig = img.getAttribute('data-original-src');
      if (orig) return orig;
      var raw = img.currentSrc || img.src || '';
      // data:image placeholder は無効として扱う
      if (raw.indexOf('data:image') === 0) return null;
      return originalSrc(raw);
    }
    return null;
  }

  // ---------------------------- 正規化 ----------------------------
  // 親 <a> の href と img の src / data-original-src が「同じ画像を指しているか」を判定する。
  // 同一とみなす条件:
  //   - href === img.src (絶対URL同士で完全一致)
  //   - href === img.data-original-src (lazy 画像 / 解像度違いの原寸 URL を href にしているケース)
  //   - originalSrc 正規化後 (size suffix を落とした) で一致
  // 「リンク先と画像が同一」と確認できたものだけポップアップ対象とする (= 記事リンク等を除外)。
  function hrefMatchesImage(href, img) {
    if (!href || !img) return false;
    var srcCandidates = [];
    var rawCandidates = [
      img.currentSrc || '',
      img.src || '',
      img.getAttribute('src') || ''
    ];
    for (var r = 0; r < rawCandidates.length; r++) {
      var raw = rawCandidates[r];
      if (raw && raw.indexOf('data:image') !== 0 && srcCandidates.indexOf(raw) === -1) {
        srcCandidates.push(raw);
      }
    }
    var orig = img.getAttribute('data-original-src');
    if (orig) srcCandidates.push(orig);
    var dataSrc = img.getAttribute('data-src');
    if (dataSrc && dataSrc.indexOf('data:image') !== 0) srcCandidates.push(dataSrc);
    if (!srcCandidates.length) return false;
    var hrefNorm = originalSrc(href);
    for (var i = 0; i < srcCandidates.length; i++) {
      var s = srcCandidates[i];
      if (s === href) return true;
      if (originalSrc(s) === hrefNorm) return true;
    }
    return false;
  }

  // <img> 側に付いている align クラスを <a> にコピーする (ない方には追加しない)。
  // SCSS の `a.image-popup.aligncenter { margin: auto }` 等は <a> 自身にクラスが
  // 付いている前提なので、 WP 記事内ブロックの <img class="aligncenter"> が <a> に
  // ラップ/採用されたとき、 img→a に align を伝播させないと中央寄せが効かない。
  var ALIGN_CLASSES = ['aligncenter', 'alignleft', 'alignright', 'alignwide', 'alignfull', 'alignnone'];
  function copyAlignClasses(img, a) {
    if (!img || !a || !img.classList || !a.classList) return;
    for (var i = 0; i < ALIGN_CLASSES.length; i++) {
      if (img.classList.contains(ALIGN_CLASSES[i])) a.classList.add(ALIGN_CLASSES[i]);
    }
  }

  // 対象を <a class="image-popup img-popup"> + <img> 構造に統一する。
  // ユーザ指示により、 ポップアップ対象は以下のいずれかのみを採用する:
  //   (a) <a class="image-popup"> / <a class="img-popup"> が、
  //       href と内側 <img> の src / data-original-src と一致する (= リンクと画像が同一)
  //   (b) <img class="image-popup"> / <img class="img-popup"> が、
  //       親 <a> の href と src/data-original-src で一致         (= リンクと画像が同一)
  //   (c) <img class="image-popup"> / <img class="img-popup">    (= img 自身に明示クラス、 a なし)
  // (a)(b) は親 <a> をそのまま流用、 (c) はその場で <a> をラップ生成して採用する。
  // 戻り値: 正規化後の <a> (失敗時 null)
  function normalizeTarget(el) {
    if (!el) return null;
    if (isExcluded(el)) return null;

    // (a) <a> 配下に <img> がある
    // 採用条件: href と内側 <img> の src / data-original-src が同一画像 (正規化後一致)。
    // - 明示的に .image-popup / .img-popup が付いていた場合: 一致しなければ別ページ
    //   リンクとみなしクラスを外す (拡大可能の見た目を消す)。
    // - クラス未指定 (WP 標準のリンク付き画像など) の場合: 一致するときだけ採用。
    //   一致しないときは何もせず通常リンクとして残す。
    if (el.tagName === 'A') {
      var hadClass = el.classList.contains('image-popup') || el.classList.contains('img-popup');
      var aImg = el.querySelector('img');
      if (!aImg) {
        if (hadClass) return null;
        return null;
      }
      // exclusion-popup / no-popup 配下の img はそもそも対象外
      if (isExcluded(aImg)) return null;
      var aHref = el.getAttribute('href') || '';
      if (!hrefMatchesImage(aHref, aImg)) {
        if (hadClass) {
          // 別ページへのリンクとして扱う。 拡大可能の見た目も外す。
          el.classList.remove('image-popup', 'img-popup');
        }
        return null;
      }
      // 一致: 採用 (クラス未指定でも image-popup を付与してスタイル/挙動を統一)
      el.classList.add('image-popup', 'img-popup');
      copyAlignClasses(aImg, el);
      return el;
    }

    // <img> 系
    if (el.tagName !== 'IMG') return null;
    // img 自身に .image-popup / .img-popup が付いていることを (b)(c) の前提とする。
    // selector で既に絞られているはずだが、 後段の lazy upgrade 経由などで素の img が
    // 到達することがあるため念のため確認する。
    var hasImgClass = el.classList && (el.classList.contains('image-popup') || el.classList.contains('img-popup'));
    if (!hasImgClass) return null;

    var parent = el.parentNode;

    // (b) 親 <a> あり: href と src/data-original-src が一致するときのみ採用
    if (parent && parent.tagName === 'A' && !isExcluded(parent)) {
      var pHref = parent.getAttribute('href') || '';
      if (parent.classList.contains('img-popup') || hrefMatchesImage(pHref, el)) {
        parent.classList.add('image-popup', 'img-popup');
        copyAlignClasses(el, parent);
        return parent;
      }
      // 親 <a> が記事リンク等の別用途 → リンク優先、 拡大対象から除外。
      // 拡大可能の見た目 (.image-popup の枠+ルーペアイコン) も外す。
      el.classList.remove('image-popup', 'img-popup');
      return null;
    }

    // (c) 裸 <img class="image-popup"> → <a class="image-popup img-popup"> でラップ
    if (!parent) return null;
    var href = el.getAttribute('data-original-src') ||
               (el.currentSrc && el.currentSrc.indexOf('data:image') !== 0 ? el.currentSrc : '') ||
               (el.src && el.src.indexOf('data:image') !== 0 ? el.src : '');
    if (!href) {
      // gp-lazy-img でまだ data-original-src も付いていないケースは
      // 一旦保留 (upgrade 後の再 attach で正規化される)
      return null;
    }
    // <picture><source><img></picture> 構造のときは img だけ取り出すと
    // <source> が孤立して srcset が機能しなくなるため、 picture ごと <a> でラップする。
    var wrapTarget = el;
    var wrapParent = parent;
    if (parent.tagName === 'PICTURE') {
      wrapTarget = parent;
      wrapParent = parent.parentNode;
      if (!wrapParent) return null;
    }
    var a = document.createElement('a');
    a.className = 'image-popup img-popup';
    a.href = originalSrc(href);
    a.setAttribute('rel', 'noopener');
    // <img> 側に付いていたグループ/キャプション属性を <a> に引き継ぐ
    // (gallery 判定や caption resolve で <a> を見るため)
    var carryAttrs = ['data-gallery-id', 'data-caption'];
    for (var c = 0; c < carryAttrs.length; c++) {
      var v = el.getAttribute(carryAttrs[c]);
      if (v !== null) a.setAttribute(carryAttrs[c], v);
    }
    // 記事内ブロックの align クラスは img→a へコピーする
    // (a が inline 表示のままだと aligncenter の margin:auto が効かないため)
    copyAlignClasses(el, a);
    wrapParent.insertBefore(a, wrapTarget);
    a.appendChild(wrapTarget);
    return a;
  }

  // ---------------------------- Gallery レジストリ ----------------------------
  // galleries[id] = [anchor, anchor, ...] - DOM 参照だけを持つ (遅延参照)
  var galleries = Object.create(null);

  // ダイアログ (.d-dialog-set) 配下の anchor は「そのダイアログ専用の gallery」に閉じ込める。
  // これがないと fetch した詳細ページの複製 anchor が元ページの default gallery に merge され、
  // 同一画像が 2 枚並んでスワイプできる不具合になる。
  function modalScopePrefix(el) {
    if (!el || !el.closest) return '';
    var modal = el.closest('.d-dialog-set');
    if (!modal) return '';
    if (!modal.id) {
      modal.id = 'moko-modal-' + Math.floor(Math.random() * 1e9).toString(36);
    }
    return 'modal:' + modal.id + ':';
  }

  function inferGalleryId(el) {
    var scope = modalScopePrefix(el);
    var explicit = el.getAttribute('data-gallery-id') ||
      (el.closest && el.closest('[data-gallery-id]') && el.closest('[data-gallery-id]').getAttribute('data-gallery-id'));
    if (explicit) return scope + explicit;
    var wpGallery = el.closest && el.closest('.wp-block-gallery, .gallery');
    if (wpGallery) {
      if (!wpGallery.dataset.mokoGalleryAuto) {
        wpGallery.dataset.mokoGalleryAuto = 'gallery-' + Math.floor(Math.random() * 1e9).toString(36);
      }
      return scope + wpGallery.dataset.mokoGalleryAuto;
    }
    return scope + DEFAULT_GALLERY_ID;
  }

  // 配列から DOM 非接続 (= document.contains が false) の anchor を落とす。
  // ダイアログの再描画で複製 anchor が差し替わるたび、 過去の複製が galleries に
  // 残ると gallery 配列が膨張し、 index も壊れる。 登録直前に prune する。
  function pruneGallery(galleryId) {
    var arr = galleries[galleryId];
    if (!arr || !arr.length) return arr || [];
    var kept = [];
    for (var i = 0; i < arr.length; i++) {
      var a = arr[i];
      if (a && document.contains(a)) {
        kept.push(a);
        a.setAttribute(CONFIG.indexAttr, String(kept.length - 1));
      }
    }
    galleries[galleryId] = kept;
    return kept;
  }

  function registerInGallery(anchor) {
    var galleryId = inferGalleryId(anchor);
    if (!galleries[galleryId]) galleries[galleryId] = [];
    var arr = pruneGallery(galleryId);
    if (arr.indexOf(anchor) !== -1) {
      anchor.setAttribute(CONFIG.galleryAttr, galleryId);
      anchor.setAttribute(CONFIG.indexAttr, String(arr.indexOf(anchor)));
      return;
    }
    arr.push(anchor);
    anchor.setAttribute(CONFIG.galleryAttr, galleryId);
    anchor.setAttribute(CONFIG.indexAttr, String(arr.length - 1));
  }

  // ダイアログ内 (= 引数 root 配下) の anchor を galleries から抜く。
  // dialog.js の innerHTML 差し替え直前 / close 時に呼ぶ想定。
  function destroyScope(root) {
    if (!root) return;
    var keys = Object.keys(galleries);
    for (var k = 0; k < keys.length; k++) {
      var gid = keys[k];
      var arr = galleries[gid];
      if (!arr || !arr.length) continue;
      var kept = [];
      for (var i = 0; i < arr.length; i++) {
        var a = arr[i];
        if (a && root.contains(a)) continue;
        kept.push(a);
        if (a) a.setAttribute(CONFIG.indexAttr, String(kept.length - 1));
      }
      if (kept.length) galleries[gid] = kept;
      else delete galleries[gid];
    }
  }

  // ---------------------------- Overlay (singleton) ----------------------------
  var overlayEl = null;
  var stageEl = null;
  var trackEl = null;
  var captionEl = null;
  var prevBtn = null;
  var nextBtn = null;
  var counterEl = null;
  var closeBtn = null;
  var lastFocused = null;
  var prevBodyOverflow = '';
  var hideTimer = null;

  // 状態
  var current = { galleryId: null, index: 0 };
  // 3-frame バッファ [-1, 0, +1] の <img> および スピナーラッパ
  var frameImgs = [null, null, null];
  var frameSpinners = [null, null, null];

  // 並行制御
  var isAnimating = false;     // navigate アニメ中フラグ (排他)
  var swipeMoved = false;      // ドラッグ直後の click 抑制
  var pendingDirs = 0;            // アニメ中の navigate 入力をまとめる(±方向のみ)
  var isOverlayOpen = false;   // close 後の遅延 transitionend / プリロード反映を全部弾くゲート

  // 1 段階ズーム状態 (current frame のみが対象)
  var ZOOM_SCALE = 2;
  var isZoomed = false;
  var zoomTx = 0;              // 現在の画像 transform translate X
  var zoomTy = 0;              // 同 Y
  var zoomPanStart = null;     // パンドラッグ中の {x, y, tx, ty}

  function buildOverlay() {
    if (overlayEl) return overlayEl;
    overlayEl = document.createElement('div');
    overlayEl.className = ROOT_CLASS;
    overlayEl.setAttribute('role', 'dialog');
    overlayEl.setAttribute('aria-modal', 'true');
    overlayEl.setAttribute('aria-label', '画像を拡大表示');
    overlayEl.setAttribute('hidden', '');

    var frameTpl = function (idx) {
      return '<div class="' + ROOT_CLASS + '__frame" data-frame="' + idx + '">' +
               '<div class="' + ROOT_CLASS + '__spinner" aria-hidden="true"></div>' +
               '<img alt="">' +
             '</div>';
    };

    overlayEl.innerHTML =
      '<div class="' + ROOT_CLASS + '__backdrop" data-close="1"></div>' +
      '<button type="button" class="' + ROOT_CLASS + '__nav ' + ROOT_CLASS + '__nav--prev" aria-label="前の画像">' +
        '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<button type="button" class="' + ROOT_CLASS + '__nav ' + ROOT_CLASS + '__nav--next" aria-label="次の画像">' +
        '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<figure class="' + ROOT_CLASS + '__stage">' +
        '<div class="' + ROOT_CLASS + '__track">' +
          frameTpl(-1) + frameTpl(0) + frameTpl(1) +
        '</div>' +
        '<figcaption class="' + ROOT_CLASS + '__caption"></figcaption>' +
      '</figure>' +
      '<div class="' + ROOT_CLASS + '__counter" aria-live="polite"></div>' +
      '<button type="button" class="' + ROOT_CLASS + '__close" aria-label="閉じる">' +
        '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
      '</button>';

    document.body.appendChild(overlayEl);

    stageEl   = overlayEl.querySelector('.' + ROOT_CLASS + '__stage');
    trackEl   = overlayEl.querySelector('.' + ROOT_CLASS + '__track');
    captionEl = overlayEl.querySelector('.' + ROOT_CLASS + '__caption');
    prevBtn   = overlayEl.querySelector('.' + ROOT_CLASS + '__nav--prev');
    nextBtn   = overlayEl.querySelector('.' + ROOT_CLASS + '__nav--next');
    counterEl = overlayEl.querySelector('.' + ROOT_CLASS + '__counter');
    closeBtn  = overlayEl.querySelector('.' + ROOT_CLASS + '__close');
    frameImgs = Array.from(overlayEl.querySelectorAll('.' + ROOT_CLASS + '__frame img'));
    frameSpinners = Array.from(overlayEl.querySelectorAll('.' + ROOT_CLASS + '__spinner'));

    // クリック動作:
    //   - × ボタン → 閉じる
    //   - nav ボタン → nav 処理 (別ハンドラ)
    //   - caption / counter → 何もしない
    //   - 現在画像の DOMRect 内 → ズームトグル (1段階)
    //   - それ以外 (背景/余白) → ズーム中なら縮小、 縮小済みなら閉じる
    overlayEl.addEventListener('click', function (e) {
      if (swipeMoved) { return; }
      if (isAnimating) { return; }

      var t = e.target;
      if (t.closest && t.closest('.' + ROOT_CLASS + '__close')) { close(); e.preventDefault(); return; }
      if (t.closest && t.closest('.' + ROOT_CLASS + '__nav')) return;
      if (t.closest && t.closest('.' + ROOT_CLASS + '__caption')) return;
      if (t.closest && t.closest('.' + ROOT_CLASS + '__counter')) return;

      var currentImg = frameImgs[1];
      if (currentImg && currentImg.src) {
        var r = currentImg.getBoundingClientRect();
        var inImage = (e.clientX >= r.left && e.clientX <= r.right &&
                       e.clientY >= r.top && e.clientY <= r.bottom);
        if (inImage) {
          // 画像本体クリック → ズームトグル
          if (isZoomed) zoomOut();
          else zoomIn(e.clientX, e.clientY);
          e.preventDefault();
          return;
        }
      }

      // 画像外: ズーム中ならまず縮小、 さもなくば閉じる
      if (isZoomed) {
        zoomOut();
        e.preventDefault();
        return;
      }
      close();
      e.preventDefault();
    });

    prevBtn.addEventListener('click', function (e) { e.stopPropagation(); navigate(-1); });
    nextBtn.addEventListener('click', function (e) { e.stopPropagation(); navigate(1); });

    bindPointerSwipe(stageEl);

    // navigate アニメ完了時の処理は navigate() 内の done ハンドラで完結する

    return overlayEl;
  }

  // ---------------------------- フレーム描画 ----------------------------
  function getList() { return galleries[current.galleryId] || []; }

  function showSpinner(framePos) {
    var sp = frameSpinners[framePos + 1];
    if (sp) sp.classList.add('is-active');
  }
  function hideSpinner(framePos) {
    var sp = frameSpinners[framePos + 1];
    if (sp) sp.classList.remove('is-active');
  }

  // 単一フレームに anchor の現在画像を反映する。
  // - anchor から動的に href を解決 (lazy 解決後も追随)
  // - 同じ src なら skip
  // - immediate=true の時は直接 imgEl.src を当てる (transitionend 直後の current 更新用)
  // - そうでなければ裏で new Image() でプリロードしてからフレームに反映 (隣接 frame 用)
  function setFrameAnchor(framePos, anchor, immediate) {
    var imgEl = frameImgs[framePos + 1];
    if (!imgEl) return;

    if (!anchor) {
      imgEl.removeAttribute('src');
      imgEl.setAttribute('alt', '');
      imgEl.dataset.src = '';
      imgEl.classList.remove('is-loaded');
      hideSpinner(framePos);
      return;
    }

    var url = resolveSrcFromAnchor(anchor);
    var caption = resolveCaption(anchor);

    // 既にこの src を表示中なら何もしない (alt のみ更新)
    if (url && imgEl.dataset.src === url) {
      imgEl.setAttribute('alt', caption || '');
      if (imgEl.complete && imgEl.naturalWidth > 0) {
        imgEl.classList.add('is-loaded');
        hideSpinner(framePos);
      }
      return;
    }

    // 別 src への切替
    if (!url) {
      imgEl.removeAttribute('src');
      imgEl.dataset.src = '';
      imgEl.setAttribute('alt', '');
      imgEl.classList.remove('is-loaded');
      hideSpinner(framePos);
      return;
    }

    imgEl.dataset.src = url;
    imgEl.setAttribute('alt', caption || '');

    if (immediate) {
      // current フレームの更新は即時 src を当てる。
      // キャッシュ済みなら同期で描画されるので残像が発生しない。
      // 未キャッシュなら is-loaded 待ちで opacity 0、スピナーが出る。
      imgEl.classList.remove('is-loaded');
      imgEl.src = url;
      // 即時 complete なら is-loaded を当てて opacity:1
      if (imgEl.complete && imgEl.naturalWidth > 0) {
        imgEl.classList.add('is-loaded');
        hideSpinner(framePos);
      } else {
        showSpinner(framePos);
        // onload で is-loaded 当てる
        imgEl.onload = function () {
          if (imgEl.dataset.src !== url) return;
          imgEl.classList.add('is-loaded');
          hideSpinner(framePos);
        };
        // 壊れ URL / 404 でもスピナーが残らないように onerror も用意する。
        imgEl.onerror = function () {
          if (imgEl.dataset.src !== url) return;
          hideSpinner(framePos);
        };
      }
      return;
    }

    // 隣接フレーム: プリロード後にフレームへ反映
    imgEl.classList.remove('is-loaded');
    showSpinner(framePos);

    var pre = new Image();
    pre.onload = function () {
      if (imgEl.dataset.src !== url) return;
      imgEl.src = url;
      imgEl.classList.add('is-loaded');
      hideSpinner(framePos);
    };
    pre.onerror = function () {
      if (imgEl.dataset.src !== url) return;
      hideSpinner(framePos);
      imgEl.src = url;
    };
    pre.src = url;
  }

  function render(animate) {
    var list = getList();
    var n = list.length;
    var i = current.index;

    // 表示対象の anchor 内 <img> がまだ lazy 状態なら、 サムネ側も
    // 同時に upgrade してしまう (ポップアップを閉じた時の表示一貫性のため)
    var toUpgrade = [list[i]];
    if (n > 1) {
      toUpgrade.push(list[(i - 1 + n) % n]);
      toUpgrade.push(list[(i + 1) % n]);
    }
    for (var u = 0; u < toUpgrade.length; u++) {
      var a = toUpgrade[u];
      if (!a) continue;
      var lazyImg = a.querySelector && a.querySelector('img.gp-lazy-img[data-original-src]');
      if (lazyImg) upgradeLazyImage(lazyImg);
    }

    // open 直後の render: current は即時表示してユーザーを待たせない
    setFrameAnchor( 0, list[i], true);
    setFrameAnchor(-1, n > 1 ? list[(i - 1 + n) % n] : null, false);
    setFrameAnchor( 1, n > 1 ? list[(i + 1) % n]     : null, false);

    var item = list[i];
    var caption = resolveCaption(item);
    captionEl.textContent = caption;
    captionEl.style.display = caption ? '' : 'none';

    if (n > 1) {
      counterEl.textContent = (i + 1) + ' / ' + n;
      counterEl.style.display = '';
      prevBtn.style.display = '';
      nextBtn.style.display = '';
    } else {
      counterEl.textContent = '';
      counterEl.style.display = 'none';
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    }

    trackEl.classList.toggle('is-animating', !!animate && !reducedMotion());
    trackEl.style.transform = 'translate3d(-33.3333%, 0, 0)';
  }

  // ---------------------------- 1 段階ズーム ----------------------------
  // ズームのパン範囲を計算 (画像が image-popup の表示領域からはみ出さないように)
  function clampZoomTranslate(tx, ty) {
    var imgEl = frameImgs[1];
    if (!imgEl) return { tx: 0, ty: 0 };
    var r = imgEl.getBoundingClientRect();
    var halfW = r.width * (ZOOM_SCALE - 1) / 2;
    var halfH = r.height * (ZOOM_SCALE - 1) / 2;
    if (tx > halfW) tx = halfW; else if (tx < -halfW) tx = -halfW;
    if (ty > halfH) ty = halfH; else if (ty < -halfH) ty = -halfH;
    return { tx: tx, ty: ty };
  }

  function applyZoomTransform(animate) {
    var imgEl = frameImgs[1];
    if (!imgEl) return;
    imgEl.style.transition = animate && !reducedMotion()
      ? 'transform 220ms cubic-bezier(.2,.7,.2,1), opacity 160ms cubic-bezier(.2,.7,.2,1)'
      : 'opacity 160ms cubic-bezier(.2,.7,.2,1)';
    imgEl.style.transformOrigin = 'center center';
    if (isZoomed) {
      imgEl.style.transform = 'translate3d(' + zoomTx + 'px, ' + zoomTy + 'px, 0) scale(' + ZOOM_SCALE + ')';
      imgEl.style.cursor = 'grab';
    } else {
      imgEl.style.transform = '';
      imgEl.style.cursor = 'zoom-in';
    }
  }

  function zoomIn(clickX, clickY) {
    if (!frameImgs[1]) return;
    var r = frameImgs[1].getBoundingClientRect();
    // クリック位置 (画像中央からの偏差) に応じて、 拡大したときその位置が中央に来るように移動
    // 偏差 dx = clickX - 画像中央。 scale(2) で「画像内のクリック点」を中央寄せするには
    // translateX = -dx * (scale - 1) になる
    var cx = r.left + r.width / 2;
    var cy = r.top + r.height / 2;
    var dx = (clickX !== undefined && clickX !== null) ? (clickX - cx) : 0;
    var dy = (clickY !== undefined && clickY !== null) ? (clickY - cy) : 0;
    var t = clampZoomTranslate(-dx * (ZOOM_SCALE - 1), -dy * (ZOOM_SCALE - 1));
    zoomTx = t.tx;
    zoomTy = t.ty;
    isZoomed = true;
    if (overlayEl) overlayEl.classList.add('is-zoomed');
    applyZoomTransform(true);
  }

  function zoomOut() {
    if (!isZoomed) return;
    isZoomed = false;
    zoomTx = 0;
    zoomTy = 0;
    if (overlayEl) overlayEl.classList.remove('is-zoomed');
    applyZoomTransform(true);
  }

  function resetZoomState() {
    // navigate / close 時に状態を即時クリア (アニメなし)
    isZoomed = false;
    zoomTx = 0;
    zoomTy = 0;
    if (overlayEl) overlayEl.classList.remove('is-zoomed');
    var imgEl = frameImgs[1];
    if (imgEl) {
      imgEl.style.transition = '';
      imgEl.style.transform = '';
      imgEl.style.cursor = '';
    }
  }

  // ---------------------------- navigate (加算キュー + アニメ短縮 + 入力捨てず追従) ----------------------------
  // pendingDirs: 連打中に蓄積される方向加算カウンタ (例: +3 = 次へ 3 つ追加待ち)
  // アニメ完了時にキューが残っていれば transitionDuration を 80ms に短縮して連続パラパラ再生する。
  // キューが空になった最後のステップは通常速度 280ms に戻し、ロックを解除する。
  var FAST_DURATION_MS = 80;
  var NORMAL_DURATION_MS = 280;

  function stepNavigate(direction) {
    var list = getList();
    var n = list.length;
    if (n < 2 || direction === 0) return;

    var nextIndex = (current.index + direction + n) % n;

    if (reducedMotion()) {
      current.index = nextIndex;
      render(false);
      finishAnimStep();
      return;
    }

    // キューが残っていれば速い、最後のステップなら通常速度
    var duration = (pendingDirs !== 0) ? FAST_DURATION_MS : NORMAL_DURATION_MS;
    trackEl.style.transitionDuration = duration + 'ms';

    var moveTarget = direction > 0 ? '-66.6666%' : '0%';
    trackEl.classList.add('is-animating');
    trackEl.style.transform = 'translate3d(' + moveTarget + ', 0, 0)';

    var done = function (e) {
      if (e && e.propertyName && e.propertyName !== 'transform') return;
      trackEl.removeEventListener('transitionend', done);

      // close 後に遅延で発火した場合は何もしない
      if (!isOverlayOpen) {
        isAnimating = false;
        return;
      }

      // 1) インデックス確定
      current.index = nextIndex;

      // 2) transition オフ + frame 更新 + transform ジャンプ (残像対策)
      trackEl.classList.remove('is-animating');
      var prevTransition = trackEl.style.transition;
      trackEl.style.transition = 'none';

      var li = current.index;

      // 表示対象の lazy 画像を upgrade
      var navTarget = [list[li]];
      if (n > 1) {
        navTarget.push(list[(li - 1 + n) % n]);
        navTarget.push(list[(li + 1) % n]);
      }
      for (var t = 0; t < navTarget.length; t++) {
        var ta = navTarget[t];
        if (!ta) continue;
        var tlazy = ta.querySelector && ta.querySelector('img.gp-lazy-img[data-original-src]');
        if (tlazy) upgradeLazyImage(tlazy);
      }

      // current は即時 src 反映、隣接はプリロード経由
      setFrameAnchor( 0, list[li], true);
      setFrameAnchor(-1, n > 1 ? list[(li - 1 + n) % n] : null, false);
      setFrameAnchor( 1, n > 1 ? list[(li + 1) % n]     : null, false);

      // caption / counter
      var caption = resolveCaption(list[li]);
      captionEl.textContent = caption;
      captionEl.style.display = caption ? '' : 'none';
      if (n > 1) counterEl.textContent = (li + 1) + ' / ' + n;

      // 中央位置へジャンプ
      trackEl.style.transform = 'translate3d(-33.3333%, 0, 0)';

      // reflow して transition リセットを確実に反映
      // eslint-disable-next-line no-unused-expressions
      trackEl.offsetHeight;
      trackEl.style.transition = prevTransition;

      // 次ステップ消化 or 終了
      finishAnimStep();
    };
    trackEl.addEventListener('transitionend', done);
  }

  function finishAnimStep() {
    if (!isOverlayOpen) {
      isAnimating = false;
      pendingDirs = 0;
      return;
    }
    if (pendingDirs !== 0) {
      // キューが残っているので次ステップを即時開始 (アニメ短縮)
      var d = pendingDirs > 0 ? 1 : -1;
      pendingDirs -= d;
      // 次フレームで開始 (transition の値が反映される時間を確保)
      requestAnimationFrame(function () {
        if (!isOverlayOpen) { isAnimating = false; return; }
        stepNavigate(d);
      });
    } else {
      // 通常速度に戻してロック解除
      trackEl.style.transitionDuration = '';
      isAnimating = false;
    }
  }

  function navigate(direction) {
    if (direction === 0) return;
    var list = getList();
    if (list.length < 2) return;

    // navigate 開始時にズーム解除 (隣画像にズーム状態を持ち越さない)
    if (isZoomed) resetZoomState();

    if (isAnimating) {
      // 連打: 入力を絶対に捨てず加算
      pendingDirs += direction;
      return;
    }

    isAnimating = true;
    stepNavigate(direction);
  }

  // ---------------------------- スワイプ / ズーム中パン (pointer events) ----------------------------
  // 通常時は横スワイプで navigate、 ズーム中は画像を上下左右にパン (clamp 範囲内)
  function bindPointerSwipe(target) {
    var startX = 0, startY = 0, dx = 0, dy = 0;
    var axis = null; // 'h' | 'v' | null
    var mode = null; // 'swipe' | 'pan' | null
    var active = false;
    var pointerId = null;
    var startTime = 0;
    var panBaseTx = 0, panBaseTy = 0;

    target.addEventListener('pointerdown', function (e) {
      if (isAnimating) return;
      if (e.isPrimary === false) return;
      if (e.target.closest && (e.target.closest('.' + ROOT_CLASS + '__nav') || e.target.closest('.' + ROOT_CLASS + '__close'))) return;
      if (e.button !== undefined && e.button !== 0) return;

      active = true;
      axis = null;
      dx = 0; dy = 0;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startTime = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

      // ズーム中はパンモード、 通常はスワイプモード
      mode = isZoomed ? 'pan' : 'swipe';
      if (mode === 'pan') {
        panBaseTx = zoomTx;
        panBaseTy = zoomTy;
        var imgEl = frameImgs[1];
        if (imgEl) {
          imgEl.style.transition = 'none';
          imgEl.style.cursor = 'grabbing';
        }
      }
      try { target.setPointerCapture(pointerId); } catch (err) {}
    });

    target.addEventListener('pointermove', function (e) {
      if (!active || e.pointerId !== pointerId) return;
      dx = e.clientX - startX;
      dy = e.clientY - startY;

      if (mode === 'pan') {
        // ズーム中: 画像を直接ドラッグ
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) swipeMoved = true;
        if (e.cancelable) e.preventDefault();
        var c = clampZoomTranslate(panBaseTx + dx, panBaseTy + dy);
        zoomTx = c.tx;
        zoomTy = c.ty;
        var imgEl = frameImgs[1];
        if (imgEl) {
          imgEl.style.transform = 'translate3d(' + zoomTx + 'px, ' + zoomTy + 'px, 0) scale(' + ZOOM_SCALE + ')';
        }
        return;
      }

      // mode === 'swipe' : 通常スワイプ
      if (axis === null) {
        if (Math.abs(dx) < CONFIG.swipeAxisLock && Math.abs(dy) < CONFIG.swipeAxisLock) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (axis === 'v') {
          active = false;
          try { target.releasePointerCapture(pointerId); } catch (err) {}
          return;
        }
      }

      if (axis === 'h') {
        if (e.cancelable) e.preventDefault();
        swipeMoved = true;

        var list = getList();
        var n = list.length;
        var resist = 1;
        if (n <= 1) resist = CONFIG.resistanceAtEdge;

        var pctOfStage = (dx / target.clientWidth) * 33.3333 * resist;
        trackEl.classList.remove('is-animating');
        trackEl.style.transform = 'translate3d(calc(-33.3333% + ' + pctOfStage + '%), 0, 0)';
      }
    });

    var endHandler = function (e) {
      if (!active || (e && e.pointerId !== pointerId)) return;
      active = false;
      try { target.releasePointerCapture(pointerId); } catch (err) {}
      pointerId = null;

      if (mode === 'pan') {
        var imgEl = frameImgs[1];
        if (imgEl) {
          imgEl.style.cursor = 'grab';
          imgEl.style.transition = '';
        }
        // ドラッグした分が小さければ swipeMoved を立てない (タップとして扱う)
        // -> click ハンドラがズームトグルへ進む
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) {
          swipeMoved = false;
        } else if (swipeMoved) {
          setTimeout(function () { swipeMoved = false; }, 150);
        }
        mode = null;
        return;
      }

      var navigated = false;
      if (axis === 'h') {
        var now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        var duration = Math.max(1, now - startTime);
        var velocity = Math.abs(dx) / duration;
        var pastDistance = Math.abs(dx) > CONFIG.swipeDistanceThreshold;
        var pastVelocity = velocity > CONFIG.swipeVelocityThreshold && Math.abs(dx) > 12;

        if (pastDistance || pastVelocity) {
          navigate(dx > 0 ? -1 : 1);
          navigated = true;
        } else {
          trackEl.classList.add('is-animating');
          trackEl.style.transform = 'translate3d(-33.3333%, 0, 0)';
        }
      }

      if (axis === 'h' || swipeMoved) {
        swipeMoved = true;
        var clearMs = navigated ? 400 : 150;
        setTimeout(function () { swipeMoved = false; }, clearMs);
      }
      mode = null;
    };
    target.addEventListener('pointerup', endHandler);
    target.addEventListener('pointercancel', endHandler);
  }

  // ---------------------------- 開閉 / キー操作 ----------------------------
  // 矢印キーは長押し連続遷移を抑制 (e.repeat 無視)。
  // ESC は 1) ズーム中ならズーム解除 2) さもなくば close の 2 段階。
  // Tab / Shift+Tab は overlay 内にフォーカスを閉じ込める (aria-modal=true 宣言済み)。
  function onKeyDown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (isZoomed) zoomOut();
      else close();
      return;
    }
    if (e.key === 'Tab' || e.keyCode === 9) {
      trapTab(e);
      return;
    }
    if (e.key === 'ArrowLeft' || e.keyCode === 37) {
      if (e.repeat) { e.preventDefault(); return; }
      navigate(-1);
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowRight' || e.keyCode === 39) {
      if (e.repeat) { e.preventDefault(); return; }
      navigate(1);
      e.preventDefault();
      return;
    }
  }

  // overlay 内のフォーカス可能要素一覧 (順序通り)。
  // close / prev / next の 3 ボタンが主体だが、 表示中でない (display:none) ものは除外する。
  function getFocusableInOverlay() {
    if (!overlayEl) return [];
    var candidates = overlayEl.querySelectorAll(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
    );
    var visible = [];
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      // 表示されていない要素 (style.display=none / hidden 属性) はスキップ
      if (el.offsetParent === null && el.tagName !== 'BODY') continue;
      visible.push(el);
    }
    return visible;
  }

  function trapTab(e) {
    var focusables = getFocusableInOverlay();
    if (!focusables.length) {
      e.preventDefault();
      return;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    if (e.shiftKey) {
      // Shift+Tab: 先頭にいるなら末尾に戻す
      if (active === first || !overlayEl.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // Tab: 末尾にいるなら先頭に戻す
      if (active === last || !overlayEl.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function open(galleryId, index) {
    if (!galleryId || !galleries[galleryId] || !galleries[galleryId].length) return;
    if (hideTimer !== null) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    var el = buildOverlay();
    lastFocused = document.activeElement;

    current.galleryId = galleryId;
    current.index = clampIndex((typeof index === 'number') ? index : 0, galleries[galleryId].length);
    isAnimating = false;
    pendingDirs = 0;
    isOverlayOpen = true;

    render(false);

    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    el.removeAttribute('hidden');
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    document.addEventListener('keydown', onKeyDown);

    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function close() {
    if (!overlayEl || !isOverlayOpen) return;
    if (hideTimer !== null) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    isOverlayOpen = false;
    // navigate 中の連打を全部破棄、 ズーム状態もクリア
    pendingDirs = 0;
    resetZoomState();
    overlayEl.classList.remove('is-open');
    document.body.style.overflow = prevBodyOverflow;
    document.removeEventListener('keydown', onKeyDown);

    var finishHide = function () {
      overlayEl.setAttribute('hidden', '');
      frameImgs.forEach(function (img) {
        if (!img) return;
        img.removeAttribute('src');
        img.removeAttribute('alt');
        if (img.dataset) img.dataset.src = '';
        img.classList.remove('is-loaded');
      });
      frameSpinners.forEach(function (sp) { if (sp) sp.classList.remove('is-active'); });
      isAnimating = false;
      pendingDirs = 0;
      if (lastFocused && lastFocused.focus) {
        try { lastFocused.focus({ preventScroll: true }); } catch (err) {}
      }
    };

    if (reducedMotion()) finishHide();
    else {
      hideTimer = setTimeout(function () {
        hideTimer = null;
        if (!isOverlayOpen) finishHide();
      }, 200);
    }
  }

  // ---------------------------- attach (click ハンドラ登録) ----------------------------
  function onClickAnchor(e) {
    var anchor = e.currentTarget;
    // ポップアップ対象として attach 済みの <a> は href を辿らせない。
    // gallery 解決に失敗しても、 ここに到達した時点で「ポップアップ対象」扱いなので
    // 必ず preventDefault する (a.href への遷移を絶対に起こさない)。
    e.preventDefault();
    var galleryId = anchor.getAttribute(CONFIG.galleryAttr);
    if (!galleryId) return;
    // 属性の index は登録時のもの。 その後 prune が入って再採番されている可能性が
    // あるため、 クリック時に live 配列上の実位置を使う (= detached の混入・重複を無害化)。
    var arr = pruneGallery(galleryId);
    var index = arr.indexOf(anchor);
    if (index < 0) return;
    open(galleryId, index);
  }

  function attach(rawEl) {
    if (!rawEl) return;
    if (isExcluded(rawEl)) return;
    if (rawEl.getAttribute(CONFIG.initedAttr) === '1') return;

    var anchor = normalizeTarget(rawEl);
    if (!anchor) {
      // lazy 画像が「まだ src も data-original-src も持たないケース」だけは
      // 一旦保留にする。 inited を立てると upgradeLazyImage 後の再 attach が
      // 短絡してしまい、 lazy 画像を gallery に組み込めなくなるため。
      var isPendingLazy = rawEl.tagName === 'IMG' &&
        rawEl.classList && rawEl.classList.contains('gp-lazy-img') &&
        !rawEl.hasAttribute('data-original-src');
      if (!isPendingLazy) {
        // 二重 attach 防止 (通常の null パス)
        rawEl.setAttribute(CONFIG.initedAttr, '1');
      }
      return;
    }
    // <img> 自身にも inited を立てて再 scan 時の重複を防ぐ
    if (rawEl !== anchor) rawEl.setAttribute(CONFIG.initedAttr, '1');
    if (anchor.getAttribute(CONFIG.initedAttr) === '1') return;
    anchor.setAttribute(CONFIG.initedAttr, '1');

    registerInGallery(anchor);

    anchor.setAttribute('role', 'button');
    var img = anchor.querySelector('img');
    var altText = img ? img.getAttribute('alt') : '';
    anchor.setAttribute('aria-label', (altText ? altText + 'を' : '') + '拡大表示');

    anchor.addEventListener('click', onClickAnchor);
  }

  // 大量画像 (1000+ lazy) でも初期ロードの Long Task を避けるため、
  // attach 処理を 1 フレームあたり CHUNK 件ずつに分割する。
  var SCAN_CHUNK_SIZE = 80;
  function chunkedAttach(list, onItem) {
    if (!list.length) return;
    var idx = 0;
    var step = function () {
      var end = Math.min(idx + SCAN_CHUNK_SIZE, list.length);
      for (; idx < end; idx++) {
        try { onItem(list[idx]); } catch (e) { /* 個別失敗は握り潰し */ }
      }
      if (idx < list.length) {
        // 次フレームへ
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(step);
        else setTimeout(step, 0);
      }
    };
    step();
  }

  // :has(img) が未対応のブラウザでは selector 全体の querySelectorAll が SyntaxError で
  // 失敗するため、 段階的に縮退して走査する。
  function queryTargets(root) {
    var trySelectors = [
      CONFIG.selector,
      // :has() が使えない環境向けフォールバック
      'a.image-popup, a.img-popup, img.image-popup, img.img-popup'
    ];
    for (var i = 0; i < trySelectors.length; i++) {
      try { return { list: root.querySelectorAll(trySelectors[i]), needsAnchorSweep: i > 0 }; }
      catch (e) { /* next */ }
    }
    return { list: [], needsAnchorSweep: true };
  }

  function scan(scope) {
    var root = scope || document;
    var queried = queryTargets(root);
    var list = queried.list;
    // 通常 image-popup / a タグ系 (数十〜数百件想定)。 同期で処理して問題ない範囲。
    // ただし gp-lazy-img の data-original-src 持ちは下の chunkedAttach 側で
    // rAF チャンク処理するため、 ここで同期 attach すると二重処理になる。除外する。
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (
        el &&
        el.tagName === 'IMG' &&
        el.hasAttribute &&
        el.hasAttribute('data-original-src') &&
        el.classList &&
        el.classList.contains('gp-lazy-img')
      ) continue;
      attach(el);
    }

    // :has() が使えない環境向けフォールバック: クラスなしリンク付き画像 (c) を救う。
    // a[href] を全走査し、 内側に <img> があるものだけ attach に通す
    // (採用判定は normalizeTarget() 側の (a) パスで hrefMatchesImage を必ず通す)。
    if (queried.needsAnchorSweep) {
      var anchors = root.querySelectorAll('a[href]');
      for (var ai = 0; ai < anchors.length; ai++) {
        var a = anchors[ai];
        if (a.getAttribute(CONFIG.initedAttr) === '1') continue;
        if (!a.querySelector('img')) continue;
        attach(a);
      }
    }

    // lazy 画像も事前 gallery 登録。 1000+ 件想定なので rAF チャンクで分割。
    // ただし image-popup / img-popup クラスが付いている img のみを対象にし、
    // 暗黙のクラス付与は行わない (= クラス指定がない gp-lazy-img をポップアップ対象に
    // 引き込まないようにし、 通常画像のクラス制約ルールと整合させる)。
    var lazyEligible = root.querySelectorAll(CONFIG.lazySelector + '[data-original-src]');
    chunkedAttach(lazyEligible, function (limg) {
      if (limg.getAttribute(CONFIG.initedAttr) === '1') return;
      if (isExcluded(limg)) return;
      if (!limg.classList.contains('image-popup') && !limg.classList.contains('img-popup')) return;
      attach(limg);
    });

    lazyScan(root);
  }

  // ---------------------------- Lazy Loader (gp-lazy-img 互換) ----------------------------
  var lazyObserver = null;
  function ensureLazyObserver() {
    if (lazyObserver || typeof IntersectionObserver === 'undefined') return lazyObserver;
    lazyObserver = new IntersectionObserver(function (entries, obs) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        var img = entry.target;
        obs.unobserve(img);
        upgradeLazyImage(img);
      }
    }, { root: null, rootMargin: CONFIG.rootMargin, threshold: 0.01 });
    return lazyObserver;
  }

  function upgradeLazyImage(img) {
    var src = img.getAttribute('data-src');
    var origSrc = img.getAttribute('data-original-src');
    if (!src) return;
    img.src = src;
    if (origSrc) img.setAttribute('data-original-src', origSrc);
    img.classList.remove('gp-lazy-img');

    // 拡大対象としてマーク + attach
    // 暗黙の image-popup クラス付与は行わず、 元から image-popup / img-popup が
    // 付いている img だけ拡大対象として attach する (= クラス制約ルールと整合)。
    if (!isExcluded(img) && (img.classList.contains('image-popup') || img.classList.contains('img-popup'))) {
      // 親 <a> があれば href が data:image のままになっていることが多いので
      // data-original-src で確実に上書きする
      if (img.parentNode && img.parentNode.tagName === 'A' && origSrc) {
        img.parentNode.setAttribute('href', origSrc);
      }
      attach(img);
    }
  }

  // 指定スコープ内の lazy 画像を再 observe する。
  // display:none コンテナで observer が確定済みになっていても再評価できる。
  function lazyRefresh(scope) {
    var obs = ensureLazyObserver();
    if (!obs) return;
    var root = scope || document;
    var list = root.querySelectorAll(CONFIG.lazySelector);
    for (var i = 0; i < list.length; i++) {
      try { obs.unobserve(list[i]); } catch (err) {}
      obs.observe(list[i]);
    }
  }

  function lazyScan(scope) {
    var obs = ensureLazyObserver();
    if (!obs) return;
    var root = scope || document;
    var list = root.querySelectorAll(CONFIG.lazySelector);
    for (var i = 0; i < list.length; i++) {
      if (list[i].getAttribute('data-moko-lazy-observed') === '1') continue;
      list[i].setAttribute('data-moko-lazy-observed', '1');
      obs.observe(list[i]);
    }
  }

  // スクロール停止時の保険スイープ
  var sweepLazyScrollTimer = null;
  function scheduleLazySweep() {
    if (sweepLazyScrollTimer) return;
    sweepLazyScrollTimer = setTimeout(function () {
      sweepLazyScrollTimer = null;
      runLazySweep();
    }, 220);
  }
  function runLazySweep() {
    var list = document.querySelectorAll(CONFIG.lazySelector);
    if (!list.length) return;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var sweepMargin = 1500;
    for (var i = 0; i < list.length; i++) {
      var img = list[i];
      var r = img.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      if (r.bottom < -sweepMargin || r.top > vh + sweepMargin) continue;
      upgradeLazyImage(img);
    }
  }
  window.addEventListener('scroll', scheduleLazySweep, { passive: true });
  window.addEventListener('resize', scheduleLazySweep);

  // ---------------------------- 初期化 ----------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  document.addEventListener('customDOMContentLoaded', function (event) {
    var modalWindow = event && event.detail && event.detail.modalWindow;
    scan(modalWindow);
  });

  window.MokoImagePopup = {
    scan: scan,
    refresh: function () { scan(); lazyRefresh(); },
    lazyRefresh: lazyRefresh,
    open: open,
    close: close,
    destroyScope: destroyScope,
    _galleries: galleries
  };
})();
